import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { EnquiryHeaderItem, ItemWithPriceOrMarginChanges, ItemsWithPriceOrMarginChangesResponse, PriceOrMarginChangeValue } from 'src/app/shared/models/data/enquiry.model';
import { BackendService } from 'src/app/shared/services/backend/backend.service';

type PriceOrMarginChangeField =
  'fullCost'
  | 'customerBonus'
  | 'actualFm'
  | 'actualFmAmountPerWeight'
  | 'finalPrice'
  | 'alloySurchargeValue';

@Component({
  selector: 'app-modal-price-margin-changes',
  templateUrl: './modal-price-margin-changes.component.html',
  styleUrls: ['./modal-price-margin-changes.component.scss']
})
export class ModalPriceMarginChangesComponent implements OnInit, OnDestroy {
  @Input() isMobile: boolean = false;
  @Input() theme: string = 'silver';
  @Input() enquiryNo: string = '';
  @Input() masterItems: EnquiryHeaderItem[] = [];
  @Input() allowReverse: boolean = true;
  @Input() customHeaderRenderers: any;

  @Output() onGoToOverview = new EventEmitter<void>();
  @Output() onGoToEditPrices = new EventEmitter<void>();
  @Output() onReverse = new EventEmitter<void>();

  isLoading: boolean = true;
  hasLoadError: boolean = false;
  dataSet: any[] = [];
  private readonly changeFields: PriceOrMarginChangeField[] = [
    'fullCost',
    'customerBonus',
    'actualFm',
    'actualFmAmountPerWeight',
    'finalPrice',
    'alloySurchargeValue'
  ];
  private readonly baseColumnsToShow: string[] = ['customerItemNo', 'remark', 'rawMaterialGrade.rawMaterialGradeCode', 'od', 'wt', 'id', 'quantity', 'quantityUom'];
  columnsToShow: string[] = [
    ...this.baseColumnsToShow,
    ...this.changeFields.flatMap(field => [`${field}PreviousValue`, `${field}CurrentValue`])
  ];
  columnsToHide: string[] = [];
  mergedCustomHeaderRenderers: { [key: string]: () => { template: TemplateRef<any>, context: any } } = {};
  customRenderers: { [key: string]: (row: any) => { template: TemplateRef<any>, context: any } } = {};
  private readonly headerUomByColumn: { [key: string]: string } = {
    od: 'odUom',
    wt: 'wtUom',
    id: 'idUom',
    fullCost: 'costsUom',
    actualFmAmountPerWeight: 'costsUom',
    finalPrice: 'costsUom',
    alloySurchargeValue: 'costsUom'
  };
  private subscriptions: Subscription = new Subscription();

  @ViewChild('viewCostHeader', { static: true }) viewCostHeader!: TemplateRef<any>;
  @ViewChild('viewPreviousValue', { static: true }) viewPreviousValue!: TemplateRef<any>;
  @ViewChild('viewCurrentValue', { static: true }) viewCurrentValue!: TemplateRef<any>;

  constructor(private backendService: BackendService, private translate: TranslateService) { }

  ngOnInit(): void {
    this.buildCustomHeaderRenderers();
    this.buildCustomRenderers();
    this.loadChangedItems();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onOverviewClick() {
    this.onGoToOverview.emit();
  }

  onEditPricesClick() {
    this.onGoToEditPrices.emit();
  }

  onReverseClick() {
    this.onReverse.emit();
  }

  private buildCustomHeaderRenderers() {
    this.mergedCustomHeaderRenderers = {
      ...(this.customHeaderRenderers || {})
    };

    ['od', 'wt', 'id'].forEach(column => {
      this.mergedCustomHeaderRenderers[column] = () => ({
        template: this.viewCostHeader,
        context: {
          column
        }
      });
    });

    this.changeFields.forEach(field => {
      const previousColumn = `${field}PreviousValue`;
      const currentColumn = `${field}CurrentValue`;
      this.mergedCustomHeaderRenderers[previousColumn] = () => ({
        template: this.viewCostHeader,
        context: {
          column: previousColumn
        }
      });
      this.mergedCustomHeaderRenderers[currentColumn] = () => ({
        template: this.viewCostHeader,
        context: {
          column: currentColumn
        }
      });
    });
  }

  private buildCustomRenderers() {
    this.customRenderers = {};
    this.changeFields.forEach(field => {
      const previousColumn = `${field}PreviousValue`;
      const currentColumn = `${field}CurrentValue`;
      this.customRenderers[previousColumn] = (row: any) => ({
        template: this.viewPreviousValue,
        context: {
          $implicit: row,
          column: previousColumn
        }
      });
      this.customRenderers[currentColumn] = (row: any) => ({
        template: this.viewCurrentValue,
        context: {
          $implicit: row,
          column: currentColumn,
          previousColumn: previousColumn
        }
      });
    });
  }

  getHeaderColumnLabel(column: string): string {
    const baseLabel = this.getTranslatedColumnLabelWithoutUom(column);
    const uomColumn = this.getHeaderUomColumn(column);
    const uomValue = this.getHeaderColumnUom(uomColumn);
    return uomValue ? `${baseLabel} (${uomValue})` : baseLabel;
  }

  private loadChangedItems() {
    if (!this.enquiryNo) {
      this.isLoading = false;
      this.dataSet = [];
      return;
    }

    this.isLoading = true;
    this.hasLoadError = false;
    this.subscriptions.add(this.backendService.getEnquiryPriceOrMarginChanges(this.enquiryNo).subscribe({
      next: (res) => {
        const changes = this.extractChanges(res);
        this.dataSet = this.joinMasterData(changes);
        this.isLoading = false;
      },
      error: () => {
        this.hasLoadError = true;
        this.dataSet = [];
        this.isLoading = false;
      }
    }));
  }

  private extractChanges(response: ItemsWithPriceOrMarginChangesResponse | any): ItemWithPriceOrMarginChanges[] {
    const rawItems: ItemWithPriceOrMarginChanges[] =
      Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response)
          ? response
          : [];

    return rawItems.map(item => this.normalizeChangeItemKeys(item));
  }

  private normalizeChangeItemKeys(item: ItemWithPriceOrMarginChanges): ItemWithPriceOrMarginChanges {
    const raw = item as Record<string, unknown>;
    return {
      itemNo: this.normalizeOptionalString(raw['itemNo']),
      fullCost: this.asPriceOrMarginChangeValue(raw['fullCost']),
      customerBonus: this.asPriceOrMarginChangeValue(raw['customerBonus']),
      actualFm: this.asPriceOrMarginChangeValue(raw['actualFm']),
      actualFmAmountPerWeight: this.asPriceOrMarginChangeValue(raw['actualFmAmountPerWeight'] ?? raw['actualFmAmountPerWeight ']),
      finalPrice: this.asPriceOrMarginChangeValue(raw['finalPrice']),
      alloySurchargeValue: this.asPriceOrMarginChangeValue(raw['alloySurchargeValue'])
    };
  }

  private joinMasterData(changes: ItemWithPriceOrMarginChanges[]): any[] {
    const masterByItemNo = new Map<string, EnquiryHeaderItem>();
    (this.masterItems || []).forEach(item => {
      const key = this.normalizeItemNo(item?.itemNo);
      if (!key) {
        return;
      }
      masterByItemNo.set(key, item);
    });

    return (changes || [])
      .map(change => {
        const key = this.normalizeItemNo(change?.itemNo);
        const masterItem = masterByItemNo.get(key);
        const dataSetRow: any = {
          ...(masterItem || {}),
          itemNo: masterItem?.itemNo ?? change?.itemNo ?? '',
          customerItemNo: masterItem?.customerItemNo ?? '',
          'rawMaterialGrade.rawMaterialGradeCode': masterItem?.rawMaterialGrade?.rawMaterialGradeCode ?? ''
        };

        this.changeFields.forEach(field => {
          const value = change[field];
          dataSetRow[`${field}PreviousValue`] = this.isPriceOrMarginChangeValue(value) ? (value.previousValue ?? null) : null;
          dataSetRow[`${field}CurrentValue`] = this.isPriceOrMarginChangeValue(value) ? (value.currentValue ?? null) : null;
        });

        return dataSetRow;
      })
      .sort((a, b) => this.compareItemNo(a.itemNo, b.itemNo));
  }

  private normalizeItemNo(itemNo: any): string {
    return (itemNo ?? '').toString().trim();
  }

  private compareItemNo(itemNoA: any, itemNoB: any): number {
    const a = this.normalizeItemNo(itemNoA);
    const b = this.normalizeItemNo(itemNoB);
    const numericA = Number(a);
    const numericB = Number(b);
    if (Number.isFinite(numericA) && Number.isFinite(numericB)) {
      return numericA - numericB;
    }
    return a.localeCompare(b);
  }

  private isPriceOrMarginChangeValue(value: any): value is PriceOrMarginChangeValue {
    return value != null
      && typeof value === 'object'
      && ('previousValue' in value || 'currentValue' in value);
  }

  hasValueChanged(row: any, previousColumn: string, currentColumn: string): boolean {
    const previousValue = row?.[previousColumn];
    const currentValue = row?.[currentColumn];

    if (previousValue == null && currentValue == null) {
      return false;
    }

    if (previousValue == null || currentValue == null) {
      return true;
    }

    return Number(previousValue) !== Number(currentValue);
  }

  formatGridValue(value: any): string {
    if (value == null || value === '') {
      return '-';
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue.toFixed(2);
    }

    return String(value);
  }

  private getHeaderUomColumn(column: string): string {
    const baseColumn = column.replace(/(PreviousValue|CurrentValue)$/, '') as PriceOrMarginChangeField;
    return this.headerUomByColumn[baseColumn] ?? '';
  }

  private getHeaderColumnUom(uomColumn?: string): string {
    if (!uomColumn) {
      return '';
    }

    const fromGrid = this.dataSet.find(item => !!item?.[uomColumn])?.[uomColumn];
    if (fromGrid) {
      return fromGrid;
    }

    const fromItems = this.masterItems?.find(item => !!item?.[uomColumn])?.[uomColumn];
    return fromItems ?? '';
  }

  private getTranslatedColumnLabelWithoutUom(column: string): string {
    const translationKey = 'grid.' + column.charAt(0).toUpperCase() + column.slice(1);
    const translatedLabel = this.translate.instant(translationKey);
    return this.stripHardcodedUomSuffix(translatedLabel);
  }

  private stripHardcodedUomSuffix(label: string): string {
    return (label || '').replace(/\s*\(?[^\s()]+\/[^\s()]+\)?\s*$/, '').trim();
  }

  private asPriceOrMarginChangeValue(value: unknown): PriceOrMarginChangeValue | undefined {
    if (!this.isPriceOrMarginChangeValue(value)) {
      return undefined;
    }
    return {
      previousValue: value.previousValue ?? null,
      currentValue: value.currentValue ?? null
    };
  }

  private normalizeOptionalString(value: unknown): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : undefined;
  }
}
